import {
    AckoMainTheme,
    ThemeProvider,
} from '@acko-tech/building-blocks.ui.theme';
import axios from 'axios';
import { useRouter } from 'next/router';
import { useContext, useEffect, useRef, useState } from 'react';
import { MPosContext } from 'utils/context';
import { getImageBasePath } from 'utils/generics';
import { SegmentPages, SegmentViewEvents } from 'utils/segment/constants';
import { getDomainOrigin, getAxiosConfig } from 'utils/serverConfig';
import { PageLoader } from 'utils/styles';
import ErrorPage from 'widgets/errorScreen';
import Header from 'widgets/header';
import { pageEvent } from '@acko-tech/building-blocks.ui.acko-analytics';
import SuccessV1 from './successV1';
import FailureV1 from './failureV1';
import { useFetchParams } from 'hooks/useFetchParams';
import PendingV1 from './pendingV1';
import * as Styles from './styles';
import { createPaymentStatusPoller } from './paymentStatusPoller';

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPTS = 3;

const ThankYouV1 = () => {
    const router = useRouter();
    const { partnerId, dealerType } = useContext(MPosContext);

    const { payment_order_ekey } = router.query;
    const { lob } = useFetchParams();

    const [paymentStatus, setPaymentStatus] = useState({
        id: Date.now(),
        status: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const callCount = useRef(0);

    useEffect(() => {
        // Each effect run polls for a fresh (partnerId, payment_order_ekey)
        // pair (e.g. once router.query hydrates) — start from a clean
        // attempt count instead of carrying over whatever a previous run
        // left behind on this ref.
        callCount.current = 0;

        let intervalId: NodeJS.Timeout | null = null;

        const stopInterval = () => {
            if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };

        // Guards against the race that caused "Oops. Something went wrong":
        // a call already in-flight (e.g. started just before a success
        // response landed) could resolve/fail afterwards — often with a
        // 410 once the backend considers the order finalized — and
        // clobber a perfectly good success/failure state. The serialize /
        // final-status / abort guards live in `paymentStatusPoller` so
        // they can be unit tested directly.
        const poller = createPaymentStatusPoller(
            (signal) =>
                axios({
                    method: 'GET',
                    url: `${getDomainOrigin()}/api/mpos/partner/${partnerId}/payment`,
                    params: {
                        payment_order_ekey,
                        user_type: 'dealer',
                    },
                    signal,
                    ...getAxiosConfig(),
                }),
            {
                onStatus: (data) => {
                    setErrorMessage('');
                    const eventName =
                        data === 'success'
                            ? SegmentViewEvents.VIEW_PAYMENT_SUCCESS
                            : SegmentViewEvents.VIEW_PAYMENT_FAILURE;
                    pageEvent(eventName, {
                        page: SegmentPages.PAYMENT,
                        partner: partnerId,
                        product: 'pos',
                        journey: 'buy',
                        platform: 'mweb',
                        type: dealerType,
                    });
                    setPaymentStatus(() => ({
                        id: Date.now(),
                        status: data,
                    }));
                },
                onError: () => {
                    setErrorMessage('Oops. Something went wrong.');
                },
                // Only real, completed attempts land here — an aborted
                // poll (cancelled mid-flight) never reaches this callback,
                // so it can no longer inflate callCount.
                onAttemptSettled: (attemptCount) => {
                    callCount.current = attemptCount;
                    setIsLoading(false);
                },
                onDone: stopInterval,
            },
            MAX_POLL_ATTEMPTS
        );

        poller.poll();
        intervalId = setInterval(() => {
            poller.poll();
        }, POLL_INTERVAL_MS);

        return () => {
            stopInterval();
            poller.stop();
        };
    }, [partnerId, payment_order_ekey, dealerType]);

    if (isLoading) {
        return (
            <>
                <Header />
                <PageLoader src={getImageBasePath('/gif/pageLoader.gif')} />
            </>
        );
    }

    if (errorMessage) {
        return (
            <>
                <ErrorPage />;
            </>
        );
    }

    const goHome = () => {
        router.push(`/${lob}/${partnerId}/dashboard`);
    };

    return (
        <ThemeProvider theme={AckoMainTheme}>
            <Styles.Wrapper>
                <Header />
                {callCount.current < 3 ? (
                    paymentStatus.status === 'success' ? (
                        <SuccessV1 onClick={goHome} />
                    ) : paymentStatus.status === 'failure' ? (
                        <FailureV1 onClick={goHome} />
                    ) : (
                        <PageLoader
                            src={getImageBasePath('/gif/pageLoader.gif')}
                        />
                    )
                ) : // <PageLoader src={getImageBasePath('/gif/pageLoader.gif')} />
                // paymentStatus.status === 'pending'
                paymentStatus.status === 'success' ? (
                    <SuccessV1 onClick={goHome} />
                ) : paymentStatus.status === 'failure' ? (
                    <FailureV1 onClick={goHome} />
                ) : (
                    <PendingV1 onClick={goHome} />
                )}
                {/* {callCount.current < 3 && paymentStatus === '' ? (
                    <PageLoader src={getImageBasePath('/gif/pageLoader.gif')} />
                ) : paymentStatus === 'success' ? (
                    <SuccessV1 onClick={goHome} />
                ) : paymentStatus === 'failure' ? (
                    <FailureV1 onClick={goHome} />
                ) : (
                    <PendingV1 onClick={goHome} />
                )} */}
            </Styles.Wrapper>
        </ThemeProvider>
    );
};

export default ThankYouV1;
